import React from 'react';
import { observer } from 'mobx-react-lite';
import { Tooltip } from 'antd';
import { LockFilled, InfoCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import EditorStateMachine, { IElement } from '../model/EditorStateMachine';
import IframeServiceInjector from '../services/injectors/IframeServiceInjector';
import FrameEventMessanger from '../services/frames/FrameEventMessanger';
import { MOVE_TO_NEXT_SLIDE, GET_SECTION_EDITABLE_ELEMENTS, APPLY_NAVIGATION_EDIT_STYLE } from '../consts/events';
import AntdCustomButton from '../../../components/buttons/AntdCustomButton';
import SparkleIcon from './icons/SparkleIcon';
import PencilIcon from './icons/PencilIcon';
import { SmallText } from '../../../components/ui/Typography';

interface SectionEditOverlayProps {
  element: IElement;
  sectionId: number;
  iframeElement: HTMLIFrameElement;
}

const SectionEditOverlay: React.FC<SectionEditOverlayProps> = observer(
  ({ element, sectionId, iframeElement }) => {
    const { t } = useTranslation();
    const { isSectionInEditMode, selectedSectionPosition, editModeSectionId } = EditorStateMachine;
    const overlayRef = React.useRef<HTMLDivElement>(null);
    const wrapperRef = React.useRef<any>(null);

    const isThisSectionInEditMode = editModeSectionId === sectionId;
    const showButtons = !isThisSectionInEditMode;

    const handleChangeVariant = () => {
      IframeServiceInjector.getService<FrameEventMessanger>(
        'FrameEventMessanger'
      ).sendMessage(MOVE_TO_NEXT_SLIDE, {
        elementId: sectionId
      });
    };

    const handleEditElements = () => {
      const frameEventMessanger = IframeServiceInjector.getService<FrameEventMessanger>(
        'FrameEventMessanger'
      );
      
      const isThisSectionAlreadyInEditMode = isThisSectionInEditMode;
      const isAnotherSectionInEditMode = isSectionInEditMode && !isThisSectionAlreadyInEditMode;
      
      if (isAnotherSectionInEditMode) {
        EditorStateMachine.toggleSectionEditMode();
        EditorStateMachine.setEditableSectionElements([]);
        
        try {
          window.dispatchEvent(
            new CustomEvent('wizard:exit-edit-mode-cleanup', {
              detail: { removeNavigationStyles: true }
            })
          );
        } catch (e) {
          // ignore
        }
      }
      
      EditorStateMachine.toggleSectionEditMode();
      
      // Request editable elements from renderer if entering edit mode
      if (!isThisSectionAlreadyInEditMode) {
        frameEventMessanger.sendMessage(GET_SECTION_EDITABLE_ELEMENTS, {
          sectionId: sectionId
        });
      } else {
        // Clear editable elements when exiting edit mode
        EditorStateMachine.setEditableSectionElements([]);
      }
    };

    // Handle click outside to deselect section
    React.useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        
        // Check if we're on a touch device
        const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
        
        // For desktop with hover: only handle click outside when in edit mode
        // (to allow exiting edit mode by clicking outside)
        if (!isTouchDevice && !EditorStateMachine.isSectionInEditMode) {
          // Not in edit mode on desktop - let hover handle section selection
          return;
        }
        
        // Check if click is inside the overlay
        if (overlayRef.current && overlayRef.current.contains(target)) {
          return;
        }

        // Check if click is inside a LockedElementOverlay
        if (target.closest('.locked-element-overlay') !== null) {
          return;
        }

        const modalRoot = document.querySelector('.ant-modal');
        if (modalRoot && modalRoot.contains(target)) {
          return;
        }

        // Check if click is inside the iframe (clicks inside iframe will trigger SECTION_CLICKED event)
        const isClickInsideIframe = target.tagName === 'IFRAME' || target.closest('iframe') !== null;
        if (isClickInsideIframe) {
          // Let the iframe's SECTION_CLICKED event handle section selection
          // Don't deselect here as the user might be clicking within the same section
          return;
        }
        
        // Check if click is on an ElementWrapper or its children (buttons)
        // ElementWrapper has class 'pointer-events-none' or 'pointer-events-auto' (for buttons)
        const isClickOnElementWrapper = target.closest('.fixed.pointer-events-none') !== null;
        const isClickOnButton = target.closest('button') !== null;
        
        // Don't deselect if clicking on element wrappers or buttons in edit mode
        if (EditorStateMachine.isSectionInEditMode && (isClickOnElementWrapper || isClickOnButton)) {
          return;
        }

        EditorStateMachine.deselectSection();
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);

    if (!selectedSectionPosition) {
      return null;
    }
    
    if (!iframeElement) {
      return null;
    }

    const iframeRect = iframeElement.getBoundingClientRect();

    // Check if the section is completely outside the iframe viewport
    const sectionBottomInIframe = selectedSectionPosition.top + selectedSectionPosition.height;
    const isOutsideAbove = sectionBottomInIframe < 0;
    const isOutsideBelow = selectedSectionPosition.top > iframeRect.height;

    // Don't render if section is completely outside viewport
    if (isOutsideAbove || isOutsideBelow) {
      return null;
    }

    // Get top position relative to the iframe element (convert iframe viewport coords to document coords)
    let top = selectedSectionPosition.top + iframeRect.top;
    let left = selectedSectionPosition.left + iframeRect.left;
    let height = selectedSectionPosition.height;
    const width = selectedSectionPosition.width;

    if (top < iframeRect.top) {
      height = height - (iframeRect.top - top);
      top = iframeRect.top;
    }

    const bottom = top + height;
    const iframeBottom = iframeRect.top + iframeRect.height;
    if (bottom > iframeBottom) {
      height = height - (bottom - iframeBottom);
    }

    // Don't render if height became zero or negative
    if (height <= 0) {
      return null;
    }

    

    // Hide Change Variant button for virtual section -1 (footer/contact)
    const hideChangeVariantForSpecial = sectionId === -1;

    // The locked element info now comes from the renderer, so no need for manual DOM checks here.
    // If locked overlay should be shown, it will be rendered via LockedElementOverlay component.

    // Normal section overlay with buttons
    return (
      <div
        ref={(ref) => {
          overlayRef.current = ref;
          
          const WrapperSynchronizer = IframeServiceInjector.getService<any>(
            'WrapperSynchronizer'
          );

          if (ref === null) {
            if (wrapperRef.current) {
              WrapperSynchronizer.unregisterElement(`section-${sectionId}`);
              wrapperRef.current = null;
            }
            return;
          }

          // Create PageElementWrapper class inline (same as ElementWrapper pattern)
          class SectionPageWrapper {
            constructor(
              private htmlElement: HTMLElement,
              private iframeElement: HTMLIFrameElement
            ) {}

            updateElementPosition(pos: any, iframeRect: any): void {
              // Check if the section is completely outside the iframe viewport
              const sectionBottomInIframe = pos.top + pos.height;
              const isOutsideAbove = sectionBottomInIframe < 0;
              const isOutsideBelow = pos.top > iframeRect.height;

              if (isOutsideAbove || isOutsideBelow) {
                this.htmlElement.style.display = 'none';
                return;
              } else {
                this.htmlElement.style.display = 'block';
              }

              // Get top position relative to the iframe element
              let top = pos.top + iframeRect.top;
              let left = pos.left + iframeRect.left;
              let height = pos.height;
              const width = pos.width;

              // "Cut off" the overlay if section is partially scrolled outside iframe viewport (top)
              if (top < iframeRect.top) {
                height = height - (iframeRect.top - top);
                top = iframeRect.top;
              }

              // "Cut off" the overlay if section is partially scrolled outside iframe viewport (bottom)
              const bottom = top + height;
              const iframeBottom = iframeRect.top + iframeRect.height;
              if (bottom > iframeBottom) {
                height = height - (bottom - iframeBottom);
              }

              this.htmlElement.style.top = top + 'px';
              this.htmlElement.style.left = left + 'px';
              this.htmlElement.style.width = width + 'px';
              this.htmlElement.style.height = height + 'px';
            }
          }

          wrapperRef.current = new SectionPageWrapper(ref, iframeElement);
          const wrapperId = `section-${sectionId}`;
          WrapperSynchronizer.registerElement(
            { element: wrapperRef.current },
            wrapperId
          );
        }}
        onMouseLeave={(e) => {
          const relatedTarget = e.relatedTarget as HTMLElement;
          const currentTarget = e.currentTarget as HTMLElement;
          
          if (relatedTarget && currentTarget.contains(relatedTarget)) {
            return;
          }
          
          // Don't deselect if mouse is going to the iframe
          const isGoingToIframe = relatedTarget?.tagName === 'IFRAME' || relatedTarget?.closest('iframe');
          if (!isGoingToIframe) {
            EditorStateMachine.deselectSection();
          }
        }}
        data-section-overlay="true"
        className="fixed"
        style={{
          top: `${top}px`,
          left: `${left}px`,
          width: `${width}px`,
          height: `${height}px`,
          border: isThisSectionInEditMode ? 'none' : '3px solid #1890ff',
          pointerEvents: 'none',
          zIndex: 1
        }}
      >
        {showButtons && (
          <div 
            className="absolute top-0 right-0 bg-[#1890ff] text-white px-[8px] text-sm pointer-events-auto"
            onMouseLeave={(e) => {
              const relatedTarget = e.relatedTarget as HTMLElement;
              // Don't deselect if mouse is going back to the iframe
              const isGoingToIframe = relatedTarget?.tagName === 'IFRAME' || relatedTarget?.closest('iframe');
              if (!isGoingToIframe) {
                EditorStateMachine.deselectSection();
              }
            }}
          >
            {element.areaName}
          </div>
        )}
        

        {/* Buttons below section name */}
        {showButtons && (
        <div 
          className="absolute bottom-[100px] lg:bottom-[78px] left-1/2 transform -translate-x-1/2 flex gap-[18px] pointer-events-auto animate-slideUpFadeIn"
          onMouseLeave={(e) => {
            const relatedTarget = e.relatedTarget as HTMLElement;
            // Don't deselect if mouse is going back to the iframe
            const isGoingToIframe = relatedTarget?.tagName === 'IFRAME' || relatedTarget?.closest('iframe');
            if (!isGoingToIframe) {
              EditorStateMachine.deselectSection();
            }
          }}
        >
        
          {!hideChangeVariantForSpecial && (
            <AntdCustomButton className="whitespace-nowrap"
              appearance="default"
              icon={<SparkleIcon />}
              onClick={handleChangeVariant}
            >
              {t('Change variant')}
            </AntdCustomButton>
          )}
    
          <AntdCustomButton className="whitespace-nowrap"
            appearance="default"
            icon={<PencilIcon />}
            onClick={handleEditElements}
           
          >
            {t('Edit elements')}
          </AntdCustomButton>
        </div>
        )}

        {/* Info text below buttons */}
        {showButtons && (
          <SmallText className="absolute left-1/2 transform -translate-x-1/2 bottom-6 bg-black text-white px-7 py-2 rounded-lg text-xs pointer-events-none -z-10 flex items-center whitespace-nowrap animate-slideUpFadeIn">
            <InfoCircleOutlined className="text-white text-base mr-[10px]" />
            {t('Press [Esc] or click outside the preview to exit editing')}
          </SmallText>
        )}
      </div>
    );
  }
);

export default SectionEditOverlay;
